import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { logoutApp } from '@/utils/auth'
import { AlertTriangle, LogOut, RefreshCcw } from 'lucide-react'
import { useState } from 'react'
import type { FallbackProps } from 'react-error-boundary'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const ErrorFallback = ({ error }: FallbackProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showDetails, setShowDetails] = useState(false)

  const handleGoHome = () => {
    logoutApp()
    navigate('/login')
  }

  const handleReload = () => {
    window.location.reload()
  }

  return (
    <div className='min-h-screen flex items-center justify-center bg-gray-50 p-4'>
      <Card className='w-full max-w-lg shadow-xl'>
        <CardHeader className='text-center space-y-2 pb-2'>
          <div className='mx-auto bg-destructive/10 w-20 h-20 rounded-full flex items-center justify-center mb-4'>
            <AlertTriangle className='w-10 h-10 text-destructive' />
          </div>
          <CardTitle className='text-3xl font-bold text-gray-900'>
            {t('Oops! Something went wrong' as any)}
          </CardTitle>
        </CardHeader>
        <CardContent className='text-center space-y-6'>
          <p className='text-gray-600 text-lg'>
            {t('We apologize for the inconvenience. An unexpected error has occurred.' as any)}
          </p>

          <div className='flex flex-col gap-3 sm:flex-row justify-center pt-2'>
            <Button
              variant='secondary'
              onClick={handleReload}
              className='w-full sm:w-auto min-w-[140px]'
            >
              <RefreshCcw />
              {t('Try Again' as any)}
            </Button>
            <Button onClick={handleGoHome} className='w-full sm:w-auto min-w-[140px]'>
              <LogOut />
              {t('Logout' as any)}
            </Button>
          </div>

          <div className='mt-6'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setShowDetails(!showDetails)}
              className='text-gray-500 hover:text-gray-700'
            >
              {showDetails ? t('Hide Error Details' as any) : t('Show Error Details' as any)}
            </Button>

            {showDetails && (
              <div className='mt-4 text-left bg-gray-100 p-4 rounded-md overflow-auto max-h-60'>
                <p className='font-mono text-xs text-destructive break-all font-semibold'>
                  {error.message}
                </p>
                {error.stack && (
                  <pre className='mt-2 text-xs text-gray-500 whitespace-pre-wrap font-mono'>
                    {error.stack}
                  </pre>
                )}
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter className='justify-center border-t bg-gray-50/50 py-4'>
          <p className='text-xs text-gray-500'>
            {t('If the problem persists, please contact support.' as any)}
          </p>
        </CardFooter>
      </Card>
    </div>
  )
}

export default ErrorFallback
